import type { ReactNode } from "react";

import { useHomepageDatoProps, useHomepageData } from "lib/store/homepage";
import type { HomepageProps, HomepageSectionId } from "lib/types/homepage";
import {
  EventsSectionWrapper,
  FeedsAndPodcastSectionWrapper,
  HeroSectionWrapper,
  NewslettersWrapper,
  ProSectionWrapper,
  SiftedInsightsSectionWrapper,
  TalksSectionWrapper,
  TopBannerWrapper,
} from "./components/homepageWrappers";
import { normalizeHomepageSectionsOrder } from "./utils/sectionOrder";

type HomepageSectionRendererProps = {
  data: ReturnType<typeof useHomepageDatoProps>;
  homepage: HomepageProps;
};

type HomepageSectionDefinition = {
  id: HomepageSectionId;
  spacingClassName?: string;
  render: ({ data, homepage }: HomepageSectionRendererProps) => ReactNode;
};

const HOMEPAGE_SECTION_DEFINITIONS: HomepageSectionDefinition[] = [
  {
    id: "topBanner",
    render: () => <TopBannerWrapper />,
  },
  {
    id: "hero",
    spacingClassName: "pb-16",
    render: ({ data, homepage }) => (
      <HeroSectionWrapper data={data} homepage={homepage} />
    ),
  },
  {
    id: "pro",
    render: ({ data }) => <ProSectionWrapper data={data} />,
  },
  {
    id: "feedsAndPodcast",
    spacingClassName: "pb-16",
    render: ({ data, homepage }) => (
      <FeedsAndPodcastSectionWrapper data={data} homepage={homepage} />
    ),
  },
  {
    id: "newsletters",
    spacingClassName: "pb-16",
    render: () => <NewslettersWrapper />,
  },
  {
    id: "latest",
    render: ({ data }) => null /* <LatestSectionWrapper data={data} />*/,
  },
  {
    id: "siftedInsights",
    render: ({ data }) => <SiftedInsightsSectionWrapper data={data} />,
  },
  {
    id: "events",
    render: () => <EventsSectionWrapper />,
  },
  {
    id: "talks",
    render: () => <TalksSectionWrapper />,
  },
];

const HOMEPAGE_SECTION_CONFIG = new Map(
  HOMEPAGE_SECTION_DEFINITIONS.map((section) => [section.id, section])
);

export const HomepageView = () => {
  const data = useHomepageDatoProps();
  const homepage = useHomepageData();
  const normalizedOrder = normalizeHomepageSectionsOrder(data.sectionsOrder);

  // Force topBanner (position 1) and hero (position 2), ignore date-based changes as  per requirement changes. This ensures consistent user experience and simplifies content management for these key sections.
  const sectionsOrder = normalizedOrder.filter(
    (id) => id !== "topBanner" && id !== "hero"
  );
  const orderedSections = ["topBanner", "hero", ...sectionsOrder];

  return (
    <main className="w-100 overflow-x-hidden bg-mono-05 pt-[65px] text-black-rock">
      {orderedSections.map((sectionId) => {
        const section = HOMEPAGE_SECTION_CONFIG.get(
          sectionId as HomepageSectionId
        );

        if (!section) {
          return null;
        }

        return (
          <section
            key={sectionId}
            data-homepage-section={sectionId}
            className={section.spacingClassName}
          >
            {section.render({
              data,
              homepage,
            })}
          </section>
        );
      })}
    </main>
  );
};
